import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CompanySwitcher from './CompanySwitcher';

interface NavGroup {
  label: string;
  items: { path: string; label: string }[];
}

const navGroups: NavGroup[] = [
  {
    label: 'Comptabilité',
    items: [
      { path: '/dashboard', label: 'Dashboard' },
      { path: '/saisie', label: 'Écriture' },
      { path: '/transactions', label: 'Transactions' },
      { path: '/operations', label: 'Opérations' },
    ],
  },
  {
    label: 'Rapports',
    items: [
      { path: '/balance', label: 'Balance' },
      { path: '/journal', label: 'Journal' },
      { path: '/plan-comptes', label: 'Plan de comptes' },
      { path: '/trial-balance', label: 'Balance de vérification' },
      { path: '/comparaison', label: 'Comparaison' },
    ],
  },
  {
    label: 'Fiscalité',
    items: [
      { path: '/liasse', label: 'Liasse fiscale' },
      { path: '/ca12', label: 'CA12' },
      { path: '/fec', label: 'Export FEC' },
    ],
  },
  {
    label: 'Outils',
    items: [
      { path: '/cloture', label: 'Clôture' },
      { path: '/recurrents', label: 'Récurrents' },
      { path: '/import-bancaire', label: 'Import bancaire' },
      { path: '/bank-sync', label: 'Sync bancaire' },
      { path: '/cashflow', label: 'Trésorerie' },
      { path: '/audit', label: 'Justificatifs OCR' },
    ],
  },
  {
    label: 'Paramètres',
    items: [
      { path: '/clients', label: 'Clients' },
      { path: '/fournisseurs', label: 'Fournisseurs' },
      { path: '/tva-settings', label: 'Taux de TVA' },
      { path: '/auto-cat-rules', label: 'Règles auto-cat' },
      { path: '/facturation', label: 'Facturation' },
      { path: '/profil', label: 'Mon compte' },
    ],
  },
];

export default function Sidebar({ onAddCompany }: { onAddCompany?: () => void }) {
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <>
      <button
        className="fixed top-3 left-3 z-[1001] bg-blue-600 text-white border-none rounded-md px-3 py-2 text-lg cursor-pointer md:hidden"
        onClick={() => setOpen(!open)}
        aria-label="Menu"
      >
        {open ? '✕' : '☰'}
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-[999] md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <nav
        className={`fixed top-0 left-0 w-[220px] h-screen bg-slate-900 text-gray-300 flex flex-col py-4 z-[1000] transition-transform duration-200 ${
          open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <CompanySwitcher onAddCompany={onAddCompany || (() => {})} />
        <div className="flex-1 overflow-y-auto">
          {navGroups.map((group) => (
            <div key={group.label} className="mb-1">
              <div className="px-4 pt-3 pb-1 text-[0.65rem] uppercase tracking-wider text-slate-500 font-semibold">
                {group.label}
              </div>
              {group.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `block px-4 py-2 pl-6 text-sm border-l-3 transition-colors duration-150 ${
                      isActive
                        ? 'bg-blue-600/15 border-l-blue-500 text-white font-semibold'
                        : 'border-l-transparent text-slate-400 hover:bg-white/5 hover:text-slate-200'
                    }`
                  }
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
        {user && (
          <div className="border-t border-slate-700 px-4 py-3">
            <p className="text-xs text-slate-400 truncate mb-2">{user.email}</p>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 bg-slate-800 rounded-lg hover:bg-red-600/20 hover:text-red-400 transition-colors"
            >
              <span aria-hidden="true">&#x2192;</span>
              Se déconnecter
            </button>
          </div>
        )}
      </nav>
    </>
  );
}
